import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { API_BASE_URL, ENDPOINTS } from './config';
import { supabase } from './lib/supabase';
import "bootstrap/dist/css/bootstrap.min.css";

export default function AuthPage() {
  const [isRegistering, setIsRegistering] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { signIn, signUp } = useAuth();

  const [loginData, setLoginData] = useState({
    email: "",
    password: ""
  });

  const [registerData, setRegisterData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    role: "recruiter",
    company_name: "",
    industry: "",
    location: "",
    website: ""
  });

  const handleLoginInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRegisterInputChange = (e) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await signIn(loginData.email, loginData.password);
      navigate('/jd');
    } catch (error) {
      console.error("Login Error:", error);
      alert(error.message || "Invalid credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // First create the Supabase auth user with metadata
      const authData = await signUp(registerData.email, registerData.password, {
        first_name: registerData.first_name,
        last_name: registerData.last_name
      });

      // Then create the user record with additional details
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.REGISTER}`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...registerData,
          auth_id: authData.user.id  // Include the auth_id from Supabase
        }),
      });

      const result = await response.json();

      if (response.ok) {
        alert("Registration successful! Please check your email for a confirmation link.");
        setIsRegistering(false);
      } else {
        throw new Error(result.error || "Registration failed");
      }
    } catch (error) {
      console.error("Registration Error:", error);
      alert(error.message || "Registration failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotPasswordEmail, {
        //redirectTo: `${window.location.origin}/reset-password`,
        redirectTo: `https://hiyaa.in/reset-password`,
      });

      if (error) throw error;

      alert("If an account exists with this email, you will receive password reset instructions.");
      setIsForgotPassword(false);
      setForgotPasswordEmail("");
    } catch (error) {
      console.error("Forgot Password Error:", error);
      alert(error.message || "Failed to process request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isForgotPassword) {
    return (
      <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="card shadow-lg" style={{ maxWidth: '400px', width: '100%' }}>
          <div className="card-body p-5">
            <h2 className="text-center mb-4">Reset Password</h2>
            <form onSubmit={handleForgotPassword}>
              <div className="mb-3">
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="form-control"
                  disabled={isLoading}
                />
              </div>
              <button 
                type="submit" 
                className="btn btn-primary w-100 mb-3"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsForgotPassword(false);
                  setForgotPasswordEmail("");
                }}
                className="btn btn-link w-100"
                disabled={isLoading}
              >
                Back to Login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid min-vh-100">
      <div className="row min-vh-100">
        {/* Left side - Hero Section */}
        <div className="col-md-7 d-none d-md-flex align-items-center justify-content-center bg-primary bg-gradient">
          <div className="text-center text-white p-5">
            <h1 className="display-3 fw-bold mb-4">Welcome to Hiyaa.in</h1>
            <p className="lead mb-4">Your gateway to seamless recruitment and job searching</p>
            <div className="d-flex justify-content-center">
              <div className="bg-white bg-opacity-10 rounded-3 p-4 mb-4">
                <h3 className="h5 mb-3">Why Choose Hiyaa?</h3>
                <ul className="list-unstyled text-start">
                  <li className="mb-2">✓ Smart recruitment solutions</li>
                  <li className="mb-2">✓ Efficient job matching</li>
                  <li className="mb-2">✓ Professional networking</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Auth Forms */}
        <div className="col-md-5 d-flex align-items-center justify-content-center">
          <div className="card shadow-sm border-0" style={{ maxWidth: '450px', width: '100%' }}>
            <div className="card-body p-4 p-md-5">
              <h2 className="text-center mb-4">{isRegistering ? "Create Account" : "Sign In"}</h2>

              {isRegistering ? (
                <form onSubmit={handleRegister}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <input 
                        name="first_name" 
                        placeholder="First Name" 
                        required 
                        onChange={handleRegisterInputChange}
                        className="form-control"
                        disabled={isLoading}
                      />
                    </div>
                    <div className="col-md-6">
                      <input 
                        name="last_name" 
                        placeholder="Last Name" 
                        required 
                        onChange={handleRegisterInputChange}
                        className="form-control"
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                  <div className="mb-3 mt-3">
                    <input 
                      type="email" 
                      name="email" 
                      placeholder="Email" 
                      required 
                      onChange={handleRegisterInputChange}
                      className="form-control"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="mb-3">
                    <input 
                      type="password" 
                      name="password" 
                      placeholder="Password" 
                      required 
                      onChange={handleRegisterInputChange}
                      className="form-control"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="mb-3">
                    <select 
                      name="role" 
                      onChange={handleRegisterInputChange} 
                      value={registerData.role}
                      className="form-select"
                      disabled={isLoading}
                    >
                      <option value="jobseeker">Jobseeker</option>
                      <option value="recruiter">Recruiter</option>
                      <option value="admin">Admin</option>
                      <option value="hiring_manager">Hiring Manager</option>
                    </select>
                  </div>

                  {registerData.role === "recruiter" && (
                    <div className="recruiter-fields">
                      <div className="mb-3">
                        <input
                          name="company_name"
                          placeholder="Company Name"
                          required
                          onChange={handleRegisterInputChange}
                          value={registerData.company_name}
                          className="form-control"
                          disabled={isLoading}
                        />
                      </div>
                      <div className="mb-3">
                        <input
                          name="industry"
                          placeholder="Industry"
                          required
                          onChange={handleRegisterInputChange}
                          value={registerData.industry}
                          className="form-control"
                          disabled={isLoading}
                        />
                      </div>
                      <div className="mb-3">
                        <input
                          name="location"
                          placeholder="Location"
                          required
                          onChange={handleRegisterInputChange}
                          value={registerData.location}
                          className="form-control"
                          disabled={isLoading}
                        />
                      </div>
                      <div className="mb-3">
                        <input
                          name="website"
                          placeholder="Company Website"
                          type="url"
                          required
                          onChange={handleRegisterInputChange}
                          value={registerData.website}
                          className="form-control"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  )}
                  
                  <button 
                    type="submit" 
                    className="btn btn-primary w-100 mb-3"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Creating Account...
                      </>
                    ) : (
                      'Register'
                    )}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleLogin}>
                  <div className="mb-3">
                    <input 
                      type="email" 
                      name="email" 
                      placeholder="Email" 
                      required 
                      onChange={handleLoginInputChange}
                      className="form-control"
                      disabled={isLoading}
                    />
                  </div>
                  <div className="mb-3">
                    <input 
                      type="password" 
                      name="password" 
                      placeholder="Password" 
                      required 
                      onChange={handleLoginInputChange}
                      className="form-control"
                      disabled={isLoading}
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="btn btn-primary w-100 mb-3"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Signing In...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsForgotPassword(true)}
                    className="btn btn-link w-100 text-decoration-none"
                    disabled={isLoading}
                  >
                    Forgot Password?
                  </button>
                </form>
              )}

              <div className="text-center mt-4">
                <button 
                  onClick={() => setIsRegistering(!isRegistering)} 
                  className="btn btn-link text-decoration-none"
                  disabled={isLoading}
                >
                  {isRegistering ? "Already have an account? Sign In" : "New here? Create Account"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}